import { PostHogProvider, usePostHog } from "@posthog/react";
import posthog from "posthog-js";
import type { CaptureResult, PostHog, Properties } from "posthog-js";
import React from "react";
import { __POSTHOG_HOST__, __POSTHOG_PROJECT_TOKEN__ } from "../globals";

const isPostHogEnabled = Boolean(__POSTHOG_PROJECT_TOKEN__);
const allowedEvents = new Set([
  "$identify",
  "user_logged_in",
  "onboarding_flow_started",
  "onboarding_step_viewed",
  "onboarding_step_completed",
  "onboarding_step_skipped",
  "onboarding_step_skip_reversed",
  "onboarding_step_failed",
  "onboarding_flow_completed",
  "onboarding_flow_exited",
]);

const urlPropertyName = /(href|referrer|url)/i;
const ipPropertyNames = new Set([
  "geoip",
  "ip",
  "ip_address",
  "ipaddress",
  "remote_addr",
  "remote_address",
]);

function isIpProperty(propertyName: string): boolean {
  const normalizedPropertyName = propertyName.replace(/^\$/, "").toLowerCase();
  return ipPropertyNames.has(normalizedPropertyName) || normalizedPropertyName.startsWith("geoip_");
}

function stripQueryStringAndFragment(value: string): string {
  return value.replace(/[?#].*$/, "");
}

export function sanitizeProductAnalyticsProperties(properties: Properties): Properties {
  return Object.fromEntries(
    Object.entries(properties).flatMap(([propertyName, value]) => {
      if (isIpProperty(propertyName)) {
        return [];
      }

      if (typeof value === "string" && urlPropertyName.test(propertyName)) {
        return [[propertyName, stripQueryStringAndFragment(value)]];
      }

      return [[propertyName, value]];
    }),
  );
}

export function sanitizeProductAnalyticsEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return null;
  }

  // Replay snapshots are already masked by the recorder configuration. Preserve
  // PostHog's internal event shape so replay ingestion continues to work.
  if (event.event === "$snapshot") {
    return event;
  }

  if (!allowedEvents.has(event.event)) {
    return null;
  }

  return {
    ...event,
    properties: {
      ...sanitizeProductAnalyticsProperties(event.properties),
      // Defense in depth: ask ingestion to skip GeoIP enrichment. The project-level
      // "Discard client IP data" setting remains authoritative for the request IP itself.
      $geoip_disable: true,
    },
    ...(event.$set ? { $set: sanitizeProductAnalyticsProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: sanitizeProductAnalyticsProperties(event.$set_once) } : {}),
  };
}

if (isPostHogEnabled) {
  posthog.init(__POSTHOG_PROJECT_TOKEN__, {
    api_host: __POSTHOG_HOST__,
    before_send: sanitizeProductAnalyticsEvent,
    defaults: "2026-05-30",
    autocapture: false,
    capture_dead_clicks: false,
    disable_capture_url_hashes: true,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_performance: false,
    disable_session_recording: true,
    session_recording: {
      // Keep general text visible while masking fields likely to contain personal data.
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        textarea: true,
      },
    },
    disable_surveys: true,
    person_profiles: "identified_only",
    rageclick: false,
  });
}

export function ProductAnalyticsProvider({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export function usePostHogClient(): PostHog | undefined {
  const client = usePostHog();
  return isPostHogEnabled ? client : undefined;
}
